/* eslint-disable react-hooks/exhaustive-deps, react/forbid-component-props, @next/next/no-img-element */
/* global google */
import dataCollector from 'braintree-web/data-collector';
import googlePayment from 'braintree-web/google-payment';
import threeDSecure from 'braintree-web/three-d-secure';
import classNames from 'classnames';
import { Field, Formik } from 'formik';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { MERCHANT_NAME } from '../../../brands/default/config';
import {
  PAYMENT_TOP_CTA_ID,
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  PAYMENT_DOD_TRACKING_GATEWAY,
} from '../../../brands/default/constants';
import { CHECKOUT_PAYMENT_COMPONENT } from '../../../brands/default/content/components/checkout';
import { BUTTON_IMG_GOOGLE_PAY } from '../../../config/constants/images';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import BraintreeService from '../../../helpers/braintreeService';
import CustomErrorsService from '../../../helpers/customErrorsService';
import ErrorTrackingService from '../../../helpers/errorTrackingService';
import Message, { MESSAGE_TYPES } from '../../_generic/Message';
import CTAButton from '../../_generic/button/CTAButton';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import ButtonGoToPayment from './ButtonGoToPayment';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentSummary from './PaymentSummary';
import VipMessageWrapper from './VipMessageWrapper';
import VipToggleButtons from './VipToggleButtons';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentGoogle = ({
  afterError,
  atolProtected,
  basketProducts,
  currencySymbol,
  customError,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  payFinalOrderAmount,
  providerError,
  setVipOptIn,
  totalQuantity,
  travelDisableButton,
  vipOptIn = null,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  welcomeVipEnabled,
  vipPaywall,
}) => {
  const formikRef = React.useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [instance, setInstance] = useState(null);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const [threeDSecureInstance, setThreeDSecureInstance] = useState(null);

  const handleVipToggleClick = (vipOptInValue) => {
    setVipOptIn(vipOptInValue);
  };

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    toggleLoaderOff();
    // call order complete in payment component
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      // custom errors
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    } else {
      // default error message
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.errorDefaultPayment);
    }

    toggleLoaderOff();
    setPaymentInProcess(false);

    // will reload the checkout
    afterError();
  };

  const sendPayment = (tokenizedPayload) => {
    BraintreeService.preparePayment({
      basketProducts,
      credit: false,
      dataCollectorInstance,
      isWalletApplied,
      orderSummary,
      paymentMethod: PAYMENT_METHODS.card,
      tokenizationPayload: tokenizedPayload,
    });
  };

  const setupMethods = async (clientInstance) => {
    if (!clientInstance) return;
    if (threeDSecureInstance) {
      threeDSecureInstance.teardown();
      setThreeDSecureInstance(null);
    }
    try {
      const response = await threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
      if (response) {
        setThreeDSecureInstance(response);
      } else {
        ErrorTrackingService.logError(
          CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.errorDefaultPayment,
        );
        setIsLoading(false);
      }
    } catch (error) {
      notifyError(error);
    }
    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });
      if (response) {
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        setDataCollectorInstance(response);
      }
    } catch (error) {
      if (error) {
        console.error(error);

        return;
      }
    }
    try {
      const googlePaymentInstance = await googlePayment.create({
        client: clientInstance,
        googleMerchantId: '09171299690450876945',
        googlePayVersion: 2, // Optional in sandbox; if set in sandbox, this value must be a valid production Google Merchant ID
      });
      if (googlePaymentInstance) {
        setInstance(googlePaymentInstance);
        toggleLoaderOff();
      }
    } catch (error) {
      console.error(`Error creating Google Pay Instance: ${error}`);
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.errorBraintree);
    }
  };

  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.google;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    try {
      const clientInstance = await BraintreeService.getClientInstance();
      if (clientInstance) {
        setupMethods(clientInstance);
      }
    } catch (error) {
      if (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    toggleLoaderOn();
    setupBraintree();
    formikRef.current.validateForm();
  }, [payFinalOrderAmount, vipOptIn]);

  const submitPayment = async ({ amount, tokenizedPayload }) => {
    if (tokenizedPayload.details.isNetworkTokenized === false) {
      try {
        const payload = await threeDSecureInstance.verifyCard({
          amount: amount.toFixed(2).toString(),
          bin: tokenizedPayload.details.bin,
          challengeRequested: true,
          merchantName: MERCHANT_NAME,
          nonce: tokenizedPayload.nonce,
          onLookupComplete: (data, next) => {
            next();
          },
        });
        if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
          // Liablity possible, but failed challenge
          handleOrderError(
            CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.liabilityError,
          );
        } else {
          // Either the liability shift is not possible, or the liability shifted
          if (payload.nonce) tokenizedPayload.nonce = payload.nonce;
          sendPayment(tokenizedPayload);
        }
      } catch (error) {
        notifyError(error?.message);
      }
    } else {
      sendPayment(tokenizedPayload);
    }
  };

  const initiatePayment = async () => {
    BraintreeService.totalAmountToPay = payFinalOrderAmount;
    setPaymentInProcess(true);
    providerError({});
    toggleLoaderOn();
    trackEvent('googlepay_click');
    if (welcomeVipEnabled && vipOptIn) trackEvent('vip_gpaybw_click');
    if (welcomeVipEnabled && !vipOptIn) trackEvent('vip_gpaybwo_click');
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.braintree });

    const paymentDataRequest = instance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: upperCase(orderSummary?.currency),
        totalPrice: payFinalOrderAmount.toFixed(2).toString(),
        totalPriceStatus: 'FINAL',
      },
    });

    const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
    cardPaymentMethod.parameters.billingAddressRequired = true;
    cardPaymentMethod.parameters.billingAddressParameters = {
      format: 'FULL',
      phoneNumberRequired: true,
    };

    try {
      const paymentsClient = new google.payments.api.PaymentsClient({
        environment:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod'
            ? 'PRODUCTION'
            : 'TEST',
      });
      const paymentData = await paymentsClient.loadPaymentData(
        paymentDataRequest,
      );
      if (paymentData) {
        const tokenizedPayload = await instance.parseResponse(paymentData);
        if (!tokenizedPayload) {
          console.error('Error creating Google Pay Client');
          notifyError(
            CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.errorDefaultPayment,
          );
          toggleLoaderOff();

          return;
        }
        submitPayment({ amount: payFinalOrderAmount, tokenizedPayload });
      }
    } catch (error) {
      setPaymentInProcess(false);
      console.error(`Error creating Google Pay Client: ${error}`);
      ErrorTrackingService.logError(error);
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.errorDefaultPayment);
      trackEvent('googlepay_aborted');
      toggleLoaderOff();
    }
  };

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      <Formik
        enableReinitialize
        initialValues={{
          cardCvv: '',
          confirmPayment: true,
          isVipOptedIn: vipOptIn,
        }}
        innerRef={formikRef}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }
          if (!isVipUser && welcomeVipEnabled && vipOptIn === null) {
            errors.isVipOptedIn = true;
          }

          return errors;
        }}
      >
        {({ handleSubmit, isValid, isSubmitting }) => (
          <form className="" name="payment" onSubmit={handleSubmit}>
            <div className="row payment-body">
              <div className="col-12">
                {CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.formMessage}
                <PaymentAgreeTerms disabled={false} />
                {!vipPaywall && (
                  <BasketSocialCues
                    atolProtected={atolProtected}
                    className="with-margin"
                    isLighter
                    isPostCheckout={BraintreeService.isPostCheckout}
                  />
                )}
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  isPostCheckout={BraintreeService.isPostCheckout}
                  totalQuantity={totalQuantity}
                />
              </div>
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
              {!isVipUser && welcomeVipEnabled && (
                <>
                  <VipToggleButtons
                    currencySymbol={currencySymbol}
                    finalOrderAmount={finalOrderAmount}
                    isExclusiveProductInBasket={isExclusiveProductInBasket}
                    setPayButton={handleVipToggleClick}
                    vipOptInFinalOrderAmount={vipOptInFinalOrderAmount}
                  />
                  <Field
                    id="isVipOptedIn"
                    name="isVipOptedIn"
                    type="hidden"
                    value={vipOptIn || ''}
                  />
                  <div className="vip-messages">
                    {isExclusiveProductInBasket && (
                      <div className="vip-pay-button-message">
                        <Message
                          message={
                            CHECKOUT_PAYMENT_COMPONENT.paymentGoogle
                              .vipPayExclusiveDeal
                          }
                        />
                      </div>
                    )}
                    {isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentGoogle
                                .vipPayExclusiveButton
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {!isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentGoogle
                                .vipPayButtonMessage
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {vipOptIn !== null && (
                      <div className="vip-pay-button-message">
                        {
                          CHECKOUT_PAYMENT_COMPONENT.paymentGoogle
                            .vipPayButtonMessageSelected
                        }
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12">
                <div
                  className={classNames({
                    'vip-pay-button-container': !isVipUser && welcomeVipEnabled,
                  })}
                >
                  <CTAButton
                    className="full-width black-button"
                    data-qa="payWithGooglePay"
                    disabled={
                      travelDisableButton ||
                      paymentInProcess ||
                      isSubmitting ||
                      !isValid
                    }
                    onClick={() => initiatePayment()}
                  >
                    <img
                      alt={CHECKOUT_PAYMENT_COMPONENT.paymentGoogle.imageText}
                      src={BUTTON_IMG_GOOGLE_PAY}
                    />
                  </CTAButton>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
        <ButtonGoToPayment />
      </ClientOnlyPortal>
      <style jsx>{`
        .vip-pay-button-container {
          margin: 0 auto 10px auto;
          width: 75%;
        }
        .vip-messages {
          margin: 0 auto;
          padding: 0 10px;
        }
        .vip-pay-button-message {
          margin: 0 auto 10px auto;
          text-align: center;
        }
      `}</style>
    </>
  );
};

PaymentGoogle.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketProducts: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string,
  customError: PropTypes.func.isRequired,
  finalOrderAmount: PropTypes.number,
  isExclusiveProductInBasket: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  payFinalOrderAmount: PropTypes.number,
  providerError: PropTypes.func.isRequired,
  setVipOptIn: PropTypes.func,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPaywall: PropTypes.bool,
  vipPreButtonMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentGoogle;
